import { Section } from '@/common/types/lessonPlan';
import { ScopeAndSequenceLevel } from '@/common/types/scopeAndSequence';
import { getGraphemesByIds } from '@/common/utils/scopeAndSequence';
import {
  activityFactory,
  ActivityType,
  getActivityInstruction,
  getActivityScript,
  getActivitySkill,
  getFormattedActivity,
  lookupExampleWord,
  renderInstructionsAsBulletList,
} from './utils';

const escapeHtml = (str: string) => {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
};

// const wrapInDiv = (className: string, content: string) =>
//   `<div class="${className}">${content}</div>`;
export const wrapInSpan = (className: string, content: string) =>
  `<span class="${className}">${content}</span>`;
export const createHeading = (level: number, content: string) =>
  `<h${level}>${content}</h${level}>`;
export const createParagraph = (className: string, content: string) =>
  `<p class="${className}">${content}</p>`;

const generateHorizontalItems = (content: any[]) =>
  content
    .map((item) =>
      wrapInSpan(
        'content-item',
        escapeHtml(
          Array.isArray(item.value)
            ? item.value.join(', ')
            : (item.value as string),
        ),
      ),
    )
    .join(' ');

/** Sound recognition items encode pronunciation as `spelling|pronunciation`. */
const generateSoundRecognitionItems = (content: any[]) =>
  content
    .map((item) => {
      const raw = item.value as string;
      const pipeIdx = raw.indexOf('|');
      const spelling = pipeIdx >= 0 ? raw.substring(0, pipeIdx) : raw;
      const pronunciation = pipeIdx >= 0 ? raw.substring(pipeIdx + 1) : '';
      const pronunciationAttr = pronunciation
        ? ` data-pronunciation="${escapeHtml(pronunciation)}"`
        : '';
      const exampleWord = lookupExampleWord(
        spelling,
        pronunciation || undefined,
      );
      const label = exampleWord ? `${spelling} for ${exampleWord}` : spelling;
      return `<span class="content-item"${pronunciationAttr}>${escapeHtml(label)}</span>`;
    })
    .join(' ');

const generateWordChain = (content: any[]) =>
  content
    .map((item) =>
      Array.isArray(item.value)
        ? item.value
            .map((word: string) => wrapInSpan('content-item', escapeHtml(word)))
            .join(' > ')
        : wrapInSpan('content-item', escapeHtml(item.value as string)),
    )
    .join(' > ');

const activityGenerators: Record<ActivityType, (activity: Section) => string> =
  {
    [ActivityType.LETTER_SOUND_SPELLING_DRILL]: (activity) =>
      generateHorizontalItems(activity.content),
    [ActivityType.SOUND_RECOGNITION_DRILL]: (activity) =>
      generateSoundRecognitionItems(activity.content),
    [ActivityType.ORAL_SEGMENTING_DRILL]: (activity) =>
      generateHorizontalItems(activity.content),
    [ActivityType.DECODING_WORD_CHAIN]: (activity) =>
      generateWordChain(activity.content),
    [ActivityType.SPELLING_WORD_CHAIN]: (activity) =>
      generateWordChain(activity.content),
    [ActivityType.SENTENCE_READING]: (activity) =>
      generateHorizontalItems(activity.content),
    [ActivityType.SENTENCE_DICTATION]: (activity) =>
      generateHorizontalItems(activity.content),
    [ActivityType.WORD_SUMS]: (activity) =>
      generateHorizontalItems(activity.content),
    [ActivityType.WORD_READING]: (activity) =>
      generateHorizontalItems(activity.content),
    [ActivityType.SPELLING_DRILL]: (activity) =>
      generateHorizontalItems(activity.content),
    [ActivityType.ORAL_BLENDING_DRILL]: (activity) =>
      generateHorizontalItems(activity.content),
    [ActivityType.WORD_MATRIX]: () => '',
  };

/**
 * Generates a single activity block HTML.
 * The number parameter is just text in the heading for the template — not
 * part of the activity block schema.
 */
function generateActivityBlock(
  activityType: ActivityType,
  levelData: ScopeAndSequenceLevel,
  isReview: boolean,
  num: number,
): string {
  const activity = activityFactory(activityType, levelData, isReview);
  const contentGenerator =
    activityGenerators[activityType] || ((a) => JSON.stringify(a.content));

  return `<div class="activity-block" data-type="${activityType}" data-id="${activity.id}">
${createHeading(3, `${num}. ${escapeHtml(getFormattedActivity(activityType))}`)}
${createParagraph('skill', `<strong>Skill:</strong> ${escapeHtml(getActivitySkill(activityType).split(': ')[1])}`)}
${createParagraph('instructions', '<strong>Instructions:</strong>')}
${renderInstructionsAsBulletList(getActivityInstruction(activityType))}
${createParagraph('script', `<strong>Script:</strong> <em>"${escapeHtml(getActivityScript(activityType))}"</em>`)}
${createParagraph('activity-content', '<strong>Content:</strong>')}
${createParagraph('activity-content', contentGenerator(activity))}
${createParagraph('additional-notes', '<strong>Additional Notes:</strong>')}
${createParagraph('additional-notes', '')}
</div>`;
}

/**
 * Wraps two HTML blocks in a column layout.
 */
function wrapInColumns(leftHtml: string, rightHtml: string): string {
  return `<div class="column-layout"><div class="column">${leftHtml}</div><div class="column">${rightHtml}</div></div>`;
}

/**
 * Generates activity blocks, pairing them into column layouts where possible.
 */
function generateActivitySection(
  activities: ActivityType[],
  levelData: ScopeAndSequenceLevel,
  isReview: boolean,
  startNumber: number,
): string {
  let html = '';
  const blocks = activities.map((type, i) =>
    generateActivityBlock(type, levelData, isReview, startNumber + i),
  );

  for (let i = 0; i < blocks.length; i += 2) {
    if (i + 1 < blocks.length) {
      html += wrapInColumns(blocks[i], blocks[i + 1]);
    } else {
      html += blocks[i];
    }
  }

  return html;
}

export const generateInitialContent = (
  levelData: ScopeAndSequenceLevel,
): string => {
  let content = `
    <h1>Lesson Plan: Level ${levelData.level}</h1>
    <p>${escapeHtml(levelData.description)}</p>
  `;

  const newGraphemes = getGraphemesByIds(levelData.newGraphemes);
  const newGraphemeList =
    levelData.newGraphemes.length === 0
      ? 'All preceding graphemes'
      : `${newGraphemes
          .map((grapheme) => escapeHtml(grapheme.spelling))
          .join(', ')} + all preceding graphemes`;
  content += `<p class="horizontal-list">${newGraphemeList}</p>`;

  const reviewActivities = [
    ActivityType.LETTER_SOUND_SPELLING_DRILL,
    ActivityType.SOUND_RECOGNITION_DRILL,
    ActivityType.ORAL_SEGMENTING_DRILL,
    ActivityType.SPELLING_DRILL,
  ];

  const newContentActivities = [
    ActivityType.ORAL_SEGMENTING_DRILL,
    ActivityType.SPELLING_WORD_CHAIN,
    ActivityType.SPELLING_DRILL,
    ActivityType.SENTENCE_READING,
    ActivityType.SENTENCE_DICTATION,
  ];

  // Review section
  if (reviewActivities.length > 0) {
    content += '<hr /><h2>Review Section</h2>';
    content += `<p class="section-explanation">Begin the lesson by reviewing previous content. This retrieval practice helps reinforce previously learned concepts and prepares students for new material.</p>`;
    content += generateActivitySection(reviewActivities, levelData, true, 1);
  }

  // New Content section
  if (newContentActivities.length > 0) {
    content += '<hr /><h2>New Content</h2>';
    content += `<p class="section-explanation">After reviewing previous content, introduce new material in a structured sequence moving from simple to complex. Explicitly guide students through the new concepts and skills.</p>`;
    content += generateActivitySection(
      newContentActivities,
      levelData,
      false,
      reviewActivities.length + 1,
    );
  }

  return content;
};
